import styled, { css } from 'styled-components';

import { MotionBox } from '../MotionBox';

import { BannerProps, ContentMainProps, ContentProps } from './Banner.decl';

import {
  ActivityFilled,
  Box,
  CloseCircleOutlined,
  Flex,
  Icon,
  InformationFilled,
  NotificationOnFilled,
  Spacer,
  TickCircleFilled,
  Typography,
  WarningFilled,
  useTheme,
} from '@aircall/tractor';
import { color, variable } from '@helpers/style.helpers';
import { AnimatePresence } from 'framer-motion';

export const ColorOptions = {
  error: 'red',
  success: 'green',
  info: 'purple',
  warning: 'yellow',
  notification: 'blue',
} as const;

const CloseButton = styled.div`
  display: flex;
  min-width: 40px;
  min-height: 40px;
  align-items: center;
  align-self: flex-start;
  justify-content: center;
  cursor: pointer;
`;

const StyledBanner = styled(MotionBox)<Pick<BannerProps, 'type' | 'round'>>`
  position: relative;
  background: ${({ type }) =>
    ['warning'].includes(type)
      ? color[ColorOptions[type]].light
      : color[ColorOptions[type]].lighter};

  ${({ round, type }) =>
    round &&
    css`
      border: 1px solid;
      border-color: ${color[ColorOptions[type]].darker};
      border-radius: ${variable.radii[8]};
    `}
`;

const Icons = {
  error: WarningFilled,
  info: InformationFilled,
  warning: ActivityFilled,
  notification: NotificationOnFilled,
  success: TickCircleFilled,
};

function ContentBase({ customIcon, textVariant, type, text, textAlign }: ContentMainProps) {
  const { colors, space } = useTheme();

  return (
    <Spacer space='s' alignItems='center'>
      {customIcon || (
        <Icon component={Icons[type]} size={space.m} color={colors[ColorOptions[type]].darker} />
      )}
      <Typography variant={textVariant || 'subheading'} lineHeight='20px' textAlign={textAlign}>
        {text}
      </Typography>
    </Spacer>
  );
}

function Content({ additonalContent, textAlign, 'data-test': dataTest, ...rest }: ContentProps) {
  const isArray = Array.isArray(additonalContent);

  if (isArray && additonalContent.length > 1) {
    return (
      <>
        <ContentBase {...rest} />
        <Spacer space='xxxs' mr='m' mt='xxxs' direction='vertical'>
          {additonalContent.map((subtext, index) => (
            <Typography
              key={subtext?.toString()}
              data-test={`${dataTest}-${index}`}
              variant='subheading2'
              lineHeight='20px'
              textAlign={textAlign}
              ml='xl'
            >
              {subtext}
            </Typography>
          ))}
        </Spacer>
      </>
    );
  }

  if ((isArray && additonalContent.length === 1) || typeof additonalContent === 'string') {
    const content = isArray ? additonalContent[0]?.toString() : additonalContent;

    return (
      <>
        <ContentBase {...rest} />
        <Typography
          data-test={`${dataTest}-0`}
          variant='subheading2'
          lineHeight='20px'
          textAlign={textAlign}
          ml='xl'
          mr='m'
          mt='xxxs'
        >
          {content}
        </Typography>
      </>
    );
  }

  return <ContentBase {...rest} />;
}

/**
 * @deprecated We need to replace all usages of this component with Banner from @aircall/tractor-v2. To be handled in TOOL-4453
 */
export function Banner({
  children,
  type,
  className,
  text,
  additonalContent,
  customIcon,
  textVariant,
  'data-test': dataTest = 'banner-wrapper',
  round,
  py,
  px,
  onClickCloseButton,
  showCloseButton,
  isVisible = true,
  textAlign = 'justify',
  ...otherProps
}: BannerProps) {
  const { colors, space } = useTheme();
  const padding = round ? 'xs' : 'm';

  return (
    <AnimatePresence>
      {isVisible && (
        <StyledBanner
          className={className}
          type={type}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          exit={{ opacity: 0 }}
          data-test={dataTest}
          round={round}
          {...otherProps}
        >
          <Flex
            px={px !== undefined ? px : padding}
            alignItems='center'
            width='100%'
            data-test={`${dataTest}-flex`}
          >
            <Spacer
              py={py !== undefined ? py : padding}
              space='s'
              alignItems='center'
              mr='m'
              data-test={`${dataTest}-spacer`}
            >
              <Content
                data-test={dataTest}
                text={text}
                additonalContent={additonalContent}
                type={type}
                py={py}
                padding={padding}
                customIcon={customIcon}
                textVariant={textVariant}
                textAlign={textAlign}
              />
            </Spacer>
            {showCloseButton && (
              <CloseButton onClick={onClickCloseButton}>
                <Icon
                  component={CloseCircleOutlined}
                  size={space.m}
                  color={colors.grey.dark}
                  data-test='close-button'
                />
              </CloseButton>
            )}
            {children && <Box marginLeft='auto'>{children}</Box>}
          </Flex>
        </StyledBanner>
      )}
    </AnimatePresence>
  );
}
